import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, of, Subject } from 'rxjs';
import { filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { AdditionalAttachment, NextStepsDataService } from 'src/app/next-steps/next-steps.data.service';
import { environment } from '../../../../environments/environment';
import { AppService } from '../../services/app.service';
import { PermissionService } from '../../services/permission.service';
import { PrintType, QuoteService } from '../../services/quote.service';
import { SnackBarService } from '../../services/snack-bar.service';
import { UrlService } from '../../services/url.service';
import {
    SendDocumentSettings,
    SendDocumentsViaEmailDialogComponent,
} from '../send-documents-via-email-dialog/send-documents-via-email-dialog.component';
import { FormControl, FormGroup, UntypedFormGroup, ValidationErrors } from '@angular/forms';
import { Router } from '@angular/router';
import { HttpParams } from '@angular/common/http';
import { humanizeBytes, UploaderOptions, UploadFile, UploadInput, UploadOutput } from 'ngx-uploader';
import { AuthService } from '../../services/auth.service';
import { SelectedCustomerService } from '../../services/selected-customer.service';
import { MatCheckboxChange } from '@angular/material/checkbox';

export interface PdfDownloadUrl {
    isError: boolean;
    url: string;
    type?: string;
}

type UploadFileFormControl = {
    name?: string;
    size?: string;
};

export interface UploadFileControl {
    file: UploadFile;
    control: FormControl<UploadFileFormControl>;
    uploading: any;
    queued?: boolean;
    deleting?: boolean;
    owner?: string;
}

export interface UploadAttachmentControl {
    id: number;
    documentUri: string;
    filename: string;
}

const MAX_FILE_SIZE = 10485760;

enum SchemeType {
    scheme = 'scheme',
    dwt = 'dwt',
}

export interface Recipient {
    email: string;
    department: string;
}

interface DocumentConfig {
    [key: string]: string[];
}

@Component({
    selector: 'app-documents-selection',
    templateUrl: './documents-selection.component.html',
    styleUrls: ['./documents-selection.component.scss'],
})
export class DocumentsSelectionComponent implements OnInit, OnDestroy {
    private documentsConfig: DocumentConfig = {
        '0500': ['QUOTE', 'SCHEME', 'ENERGY_LABEL'],
        6200: ['QUOTE', 'SCHEME', 'ENERGY_LABEL'],
        5000: ['QUOTE', 'SCHEME', 'ENERGY_LABEL'],
        6000: ['QUOTE', 'ENERGY_LABEL'],
        '0600': ['QUOTE', 'ENERGY_LABEL'],
        6100: ['QUOTE', 'SCHEME'],
        default: ['QUOTE', 'ENERGY_LABEL'],
    };
    private unsubscribe$: Subject<void> = new Subject<void>();

    private title: string;
    private selectedExportFormats: Set<string> = new Set<string>();

    public availableExportFormats: string[] = [];
    public energyLabelSelected = false;
    public schemeForm: UntypedFormGroup = new UntypedFormGroup({});
    public attachmentForm: FormGroup = new UntypedFormGroup({});
    public additionalAttachmentsForm: FormGroup = new UntypedFormGroup({});
    public additionalAttachments: AdditionalAttachment[] = [];
    public schemeIds: {
        primary: string[];
        secondary: string[];
        ems: string[];
    };
    public energyLabelHash: string;

    public defaultOfferType = 'OFFER';
    public quotePdfDownloads: PdfDownloadUrl[] = [{ url: '', isError: false, type: this.defaultOfferType }];
    public energyLabelPdfDownload: PdfDownloadUrl;
    private noRelevanceKey = 'energylabelnotrelevant';
    public recipients: string[] = [];
    public schemeTypes: SchemeType[] =
        this.appService.getSalesOrg() === '5000' ? [SchemeType.scheme, SchemeType.dwt] : [SchemeType.scheme];

    @Input()
    public sapDocumentId: string;
    @Input()
    public documentId: string;
    @Input()
    public latestRevision: string;
    @Input()
    public printType: PrintType;

    options: UploaderOptions = {
        allowedContentTypes: ['application/pdf'],
        concurrency: 3,
    };

    uploadFileControls: UploadFileControl[] = [];
    uploadAttachments = [];
    uploadAttachmentsIds = [];
    uploadAttachmentsSelected: UploadAttachmentControl[] = [];
    uploadAttachmentsSelectedUris = [];
    uploading = false;

    @Output() startUpload: EventEmitter<UploadInput> = new EventEmitter<UploadInput>();

    @ViewChild('input', { static: true }) input: ElementRef<HTMLInputElement>;

    private mailText$ = new BehaviorSubject<string | null>(null);

    dragging: boolean;

    get canSendEmail() {
        return (
            this.quotePdfDownloads?.length &&
            this.quotePdfDownloads.every((d) => !d.isError && d.url) &&
            !this.uploading
        );
    }

    get energyLabelRelevant() {
        return this.energyLabelHash !== this.noRelevanceKey;
    }

    get disableEnergyLabelCheckbox() {
        return !this.energyLabelPdfDownload || this.energyLabelPdfDownload.isError || !this.energyLabelPdfDownload.url;
    }

    get config(): string[] {
        return [
            ...(this.documentsConfig[this.appService.getSalesOrg()] || this.documentsConfig.default),
            ...(this.availableExportFormats || []),
        ];
    }

    constructor(
        private dataService: NextStepsDataService,
        private snackBarService: SnackBarService,
        private translateService: TranslateService,
        private urlService: UrlService,
        private quoteService: QuoteService,
        private permissionService: PermissionService,
        public dialog: MatDialog,
        public appService: AppService,
        private router: Router,
        private auth: AuthService,
        private selectedCustomerService: SelectedCustomerService
    ) {}

    public ngOnInit() {
        this.getQuote();
        this.fetchQuoteEmailText();
        this.fetchAdditionalAttachments();
        this.refreshFileList();
        this.selectedCustomerService.selectedCustomer$
            .pipe(switchMap((customer) => (customer ? this.fetchEmailRecipients(customer.number) : of([]))))
            .subscribe((emails: string[]) => {
                this.recipients = emails.sort((a, b) => a.localeCompare(b));
            });
    }

    public sendMail() {
        const selectedExportFormats = [...this.selectedExportFormats].sort((a, b) => a.localeCompare(b));
        this.mailText$
            .pipe(
                takeUntil(this.unsubscribe$),
                filter((text) => text !== null)
            )
            .subscribe((data) => {
                this.dialog.open<SendDocumentsViaEmailDialogComponent, SendDocumentSettings>(
                    SendDocumentsViaEmailDialogComponent,
                    {
                        data: {
                            selectedExportFormats,
                            energyLabelSelected: this.energyLabelSelected,
                            energyLabel: this.energyLabelHash,
                            schemes: this.getSelectedSchemes(),
                            documentId: this.documentId,
                            latestRevision: this.latestRevision,
                            offerAttachments: this.getSelectedAttachments(false),
                            additionalOfferAttachments: this.getSelectedAdditionalAttachments(),
                            printType: this.printType,
                            mailText: data,
                            mailRecipients: this.recipients,
                            schemesDwt: this.setSchemeDwt(),
                        },
                    }
                );
            });
    }

    public downloadSelectionUrl() {
        const params = new HttpParams({
            fromObject: {
                application: 'quote-assist',
                title: this.title,
                salesOrg: this.appService.getSalesOrg(),
                language: this.appService.getLanguageOnly(),
                country: this.appService.getLocation(),
                printType: this.printType,
                offerTypes: this.quotePdfDownloads.map((d) => d.type),
                exportFormats: [...this.selectedExportFormats],
                schemes: this.getSelectedSchemes(),
                ...(this.energyLabelSelected && { energyLabel: this.energyLabelHash }),
                archiveDocumentUris: this.getSelectedAttachments(true),
                offerAttachments: this.getSelectedAdditionalAttachments().map((a) => a.id),
                schemesDwt: this.setSchemeDwt(),
            },
        });
        return `${environment.http.quotation}files/${this.sapDocumentId}/zip?${params.toString()}`;
    }

    public getViBooksUrl() {
        const viBooksBaseUrl = environment.http.viBooksBaseUrl;
        return `${viBooksBaseUrl}/${this.appService.getLocation()}/${this.appService.getLanguageOnly()}?salesDocumentNumber=${
            this.sapDocumentId
        }`;
    }

    public get emsSchemeUrl() {
        if (this.schemeIds?.ems?.length) {
            return `https://emstool.viessmann-climatesolutions.com/ews/${this.schemeIds?.ems[0]}`;
        }
        return null;
    }

    public toggleAll() {
        const selected = !this.isAllSelected();
        this.selectedExportFormats = new Set<string>(selected ? this.availableExportFormats : []);
        if (this.config.includes('ENERGY_LABEL')) {
            this.energyLabelSelected = selected;
        }

        if (this.isSchemesAvailable) {
            this.toggleSchemes(selected);
        }
        this.toggleAttachments(selected);
    }

    public get isSchemesAvailable() {
        return (
            this.hasConfiguration &&
            this.config.includes('SCHEME') &&
            !!(this.schemeIds?.primary?.length || this.schemeIds?.secondary?.length)
        );
    }

    public isAllSelected() {
        return (
            this.availableExportFormats.every((type) => this.isExportFormatSelected(type)) &&
            (!this.config.includes('ENERGY_LABEL') || this.energyLabelSelected) &&
            (!this.isSchemesAvailable || this.isAllSchemesSelected()) &&
            this.isAllAttachmentsSelected()
        );
    }

    public isIndeterminate() {
        return (
            !this.isAllSelected() &&
            (this.selectedExportFormats.size ||
                this.allSchemeControls().some((c) => c.value) ||
                this.allAttachmentsControls().some((c) => c.value))
        );
    }

    public toggleAllSchemes() {
        const allSelected = this.isAllSchemesSelected();
        this.toggleSchemes(!allSelected);
    }

    public isAllSchemesSelected() {
        const controls = this.allSchemeControls();
        return !controls.length || controls.every((control) => control.value);
    }

    public isSchemesIndeterminate() {
        return !this.isAllSchemesSelected() && this.allSchemeControls().some((c) => c.value);
    }

    public schemeDownloadUrl(schemeId: string, type: string) {
        const params = new HttpParams({
            fromObject: {
                type,
                utm_source: 'quote-assist',
            },
        });
        return `${environment.http.pim}schemes/${schemeId}?${params.toString()}`;
    }

    public exportFormatDownloadUrl(format: string) {
        const params = new HttpParams({
            fromObject: {
                printType: this.printType,
                language: this.appService.getLanguageOnly(),
                country: this.appService.getLocation(),
                format,
            },
        });
        return `${environment.http.baseUrl}quote/${this.documentId}/revision/${
            this.latestRevision
        }/export?${params.toString()}`;
    }

    public isExportFormatSelected(format: string) {
        return this.selectedExportFormats.has(format);
    }

    public onExportFormatSelected(event: MatCheckboxChange, format) {
        if (event.checked) {
            this.selectedExportFormats.add(format);
        } else {
            this.selectedExportFormats.delete(format);
        }
    }

    public get hasConfiguration() {
        return this.quoteService.hasConfiguration();
    }

    private getSelectedSchemes(type: SchemeType = SchemeType.scheme) {
        return this.schemeIds
            ? [...(this.schemeIds?.primary || []), ...(this.schemeIds?.secondary || [])].filter(
                  (id) => this.schemeForm.controls[this.schemeControlName(id, type)].value
              )
            : [];
    }

    private toggleSchemes(selected: boolean) {
        this.allSchemeControls().forEach((control) => {
            control.setValue(selected);
        });
    }

    private allSchemeControls() {
        return Object.values(this.schemeForm.controls);
    }

    private getSelectedAdditionalAttachments() {
        return this.additionalAttachments.filter((a) => this.additionalAttachmentsForm.controls[a.id].value);
    }

    private getQuote() {
        this.quoteService
            .fetchQuote({ documentId: this.documentId, latestRevision: this.latestRevision }, true)
            .pipe(
                takeUntil(this.unsubscribe$),
                tap(({ quote, ownerUserName }) => {
                    this.fetchPrintOfferPdf(ownerUserName);
                    this.fetchExportTypes();
                    this.title = quote.title;
                    if (quote.status.toLowerCase() !== 'quoted') {
                        this.router.navigate(['summary'], {
                            queryParams: {
                                quickRef: quote.quickRef,
                                documentId: this.documentId,
                                latestRevision: this.latestRevision,
                                title: quote.title,
                            },
                        });
                    }
                })
            )
            .subscribe({
                next: ({ schemes, energyLabelHash, externalApp, quote }) => {
                    this.setEnergyLabelHash(energyLabelHash);
                    this.setSchemes(schemes);
                    const appTitle = this.quoteService.hasConfiguration(quote) ? '' : externalApp;
                    this.appService.appTitle$.next(appTitle);
                },
                error: () => {
                    this.onError('NEXT_STEPS.SNACK_BAR.FETCH_QUOTE.ERROR');
                },
            });
    }

    public schemeControlName(scheme: string, type: SchemeType) {
        return `${scheme}-${type}`;
    }

    private setSchemes(schemeIds?: { primary?: string; secondary?: string; ems?: string }) {
        const [primary, secondary, ems] = [schemeIds.primary, schemeIds.secondary, schemeIds.ems].map((id: string) =>
            (id || '').split(';').filter(Boolean)
        );
        this.schemeIds = {
            primary,
            secondary,
            ems,
        };
        [...primary, ...secondary].forEach((scheme) =>
            this.schemeTypes.forEach((type) => {
                this.schemeForm.addControl(this.schemeControlName(scheme, type), new FormControl<boolean>(true));
            })
        );
    }

    private setEnergyLabelHash(hash: string) {
        this.energyLabelHash = hash;
        if (!hash) {
            this.energyLabelPdfDownload = { isError: true, url: '' };
        } else if (hash !== this.noRelevanceKey) {
            // hash is present so generate energy label link
            const url = this.urlService.getUrl('pdf.energyLabel.download', { hash });

            this.energyLabelPdfDownload = {
                isError: false,
                url,
            };
        }
    }

    private fetchPrintOfferPdf(usecase: string) {
        this.dataService.fetchOfferPdf(this.sapDocumentId, this.printType, usecase).subscribe({
            next: (res) => {
                if (res.isError) {
                    this.onError('NEXT_STEPS.QUOTE_PDF.ERROR_MSG');
                }
                this.quotePdfDownloads = res.downloads
                    .sort((a, b) =>
                        a.type === this.defaultOfferType
                            ? -1
                            : b.type === this.defaultOfferType
                            ? 1
                            : a.type.localeCompare(b.type)
                    )
                    .map((d) => ({ ...d, isError: false }));
            },
            error: () => {
                this.onError('NEXT_STEPS.QUOTE_PDF.ERROR_MSG');
                this.quotePdfDownloads = [{ isError: true, url: '', type: this.defaultOfferType }];
            },
        });
    }
    private onError(translationKey: string) {
        if (this.permissionService.isUserAuthorized$.value) {
            this.snackBarService.openSnackBar({
                message: this.translateService.instant(translationKey),
                isFailure: true,
            });
        }
    }

    public ngOnDestroy() {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    // Attachments

    private getSelectedAttachments(download: boolean) {
        const uAIds = this.uploadAttachmentsIds.filter((id) => this.attachmentForm.controls[id].value);
        const uAFilter = this.uploadAttachments.filter((x) => uAIds.some((y) => y === x.id));
        this.uploadAttachmentsSelected = [];
        this.uploadAttachmentsSelectedUris = [];
        uAFilter.forEach((attachment) => {
            if (download) {
                this.uploadAttachmentsSelectedUris.push(attachment.documentUri);
            } else {
                this.uploadAttachmentsSelected.push({
                    id: attachment.id,
                    documentUri: attachment.documentUri,
                    filename: attachment.filename,
                });
            }
        });
        if (download) {
            return this.uploadAttachmentsSelectedUris;
        } else {
            return this.uploadAttachmentsSelected;
        }
    }

    private toggleAttachments(selected: boolean) {
        this.allAttachmentsControls().forEach((control) => {
            control.setValue(selected);
        });
    }

    public isAllAttachmentsSelected() {
        const controls = this.allAttachmentsControls();
        return !controls.length || controls.every((control) => control.value);
    }

    allAttachmentsControls() {
        return [
            ...Object.values(this.attachmentForm.controls),
            ...Object.values(this.additionalAttachmentsForm.controls),
        ];
    }

    setDownloadUrl(uri) {
        return this.dataService.downloadAttachment({
            documentId: this.documentId,
            latestRevision: this.latestRevision,
            documentUri: uri,
        });
    }

    // Upload

    refreshFileList(): void {
        this.dataService
            .getAttachments({
                documentId: this.documentId,
                latestRevision: this.latestRevision,
            })
            .subscribe((uploads) => {
                this.uploadFileControls = [];
                this.uploadAttachments = [];
                uploads.attachments.reverse().forEach((attachment) => {
                    const name = attachment.filename;
                    const file = {
                        id: attachment.id,
                        name,
                    };

                    this.uploadFileControls.push({
                        control: new FormControl({ name }),
                        file: <UploadFile>file,
                        uploading: false,
                    });
                    // Add seperate Attachments stuff
                    this.attachmentForm.addControl(attachment.id, new FormControl<boolean>(false));
                    this.uploadAttachments.push(attachment);
                    this.uploadAttachmentsIds.push(attachment.id);
                });
                this.addFileControl();
                this.uploading = false;
            });
    }

    addFileControl(file?: UploadFile) {
        if (file && this.uploadFileControls.length) {
            const last = this.uploadFileControls[this.uploadFileControls.length - 1];
            last.file = file;
            last.queued = true;
        }
        this.uploadFileControls.push({
            control: new FormControl<UploadFileFormControl>(undefined),
            file: undefined,
            uploading: false,
        });
    }

    protected invalid(uploadFileControl: UploadFileControl): ValidationErrors {
        if (uploadFileControl.file && uploadFileControl.file.size > MAX_FILE_SIZE) {
            this.set(uploadFileControl); // set for display
            return {
                max: { max: humanizeBytes(MAX_FILE_SIZE) },
            };
        }

        return null;
    }

    protected start(): void {
        this.uploadFileControls.forEach((uploadFile) => {
            const invalid = this.invalid(uploadFile);
            if (invalid) {
                // remove from queue
                this.startUpload.emit({ type: 'remove', id: uploadFile.file.id });
                uploadFile.control.setErrors(invalid);
            }
        });
        this.startUpload.emit({
            url: this.urlService.getUrl('next-steps.attachments', {
                documentId: this.documentId,
                latestRevision: this.latestRevision,
            }),
            type: 'uploadAll',
            method: 'POST',
            headers: {
                Authorization: `CSRF ${this.auth.csrfToken$.value}`,
            },
        });

        this.dragging = false;
    }

    protected set(uploadFileControl: UploadFileControl): void {
        const file = uploadFileControl.file;
        // prepare file data
        const name = file.name;
        const size = humanizeBytes(file.nativeFile.size);

        // set control value
        uploadFileControl.control.setValue({ name, size });
    }

    find(file: UploadFile) {
        return this.uploadFileControls.find((ufc) => ufc.file.id === file.id);
    }

    protected success(status: number): boolean {
        return status >= 200 && status < 400;
    }

    uploadInProgress(): boolean {
        return this.uploadFileControls.some((ufc) => ufc.uploading);
    }

    atLeastOneFileUploaded() {
        return this.uploadFileControls.length > 1 && this.uploadFileControls.some((ufc) => !!ufc.file);
    }

    deleteAttachment(uploadFileControl: UploadFileControl): void {
        uploadFileControl.deleting = true;
        this.uploading = uploadFileControl.deleting;
        this.dataService
            .deleteAttachment({
                documentId: this.documentId,
                latestRevision: this.latestRevision,
                id: uploadFileControl.file.id,
            })
            .subscribe(
                (deleted) => {
                    if (deleted) {
                        this.refreshFileList();
                    }
                },
                (error) => {
                    if (error) {
                        uploadFileControl.deleting = false;
                        this.uploading = uploadFileControl.deleting;
                    }
                }
            );
    }

    cancel(uploadFileControl: UploadFileControl): void {
        const index = this.uploadFileControls.indexOf(uploadFileControl);
        this.uploadFileControls.splice(index, 1);
        // reset value to allow choosing the same by button (for file only that way is available)
        if (this.input) {
            this.input.nativeElement.type = '';
            this.input.nativeElement.type = 'file';
        }
        this.startUpload.emit({ type: 'cancel', id: uploadFileControl.file.id });
    }

    onUpload(event: UploadOutput): void {
        switch (event.type) {
            case 'cancelled': {
                break;
            }
            case 'dragOver': {
                this.dragging = true;
                break;
            }
            case 'dragOut': {
                this.dragging = false;
                break;
            }
            case 'allAddedToQueue':
                return this.start();
            case 'addedToQueue': {
                this.addFileControl(event.file);
                this.uploading = true;
                break;
            }
            case 'uploading': {
                const ufc = this.find(event.file);
                ufc.uploading = event.file.progress.data;
                ufc.queued = false;
                break;
            }
            case 'done': {
                const file = event.file;
                const uploadFile = this.find(file);
                this.set(uploadFile);
                if (!this.success(file.responseStatus)) {
                    // means not uploaded
                    uploadFile.control.setErrors({ upload: file });
                }
                this.find(event.file).uploading = undefined;
                const uploadInProgress = this.uploadInProgress();
                if (!uploadInProgress) {
                    // all files have been uploaded, refresh file list from backend
                    this.refreshFileList();
                }
                break;
            }
        }
    }

    private fetchQuoteEmailText() {
        this.dataService
            .getQuoteEmailText(
                this.documentId,
                this.latestRevision,
                this.appService.getLanguageOnly(),
                this.appService.getLocation()
            )
            .subscribe((mail) => {
                this.mailText$.next(mail.text || '');
            });
    }

    private fetchEmailRecipients(customerNumber: string) {
        return this.dataService.getEmailRecipients(customerNumber).pipe(
            map((recipients: Recipient[]) => {
                const recipientEmails = recipients
                    .filter((recipient) => recipient.department !== '0056')
                    .map((recipient) => recipient.email)
                    .filter((email) => email && email.trim() !== '');
                return Array.from(new Set(recipientEmails));
            })
        );
    }

    private fetchAdditionalAttachments() {
        this.dataService
            .getAdditionalAttachments(this.appService.getLanguageOnly(), this.appService.getLocation())
            .subscribe((attachments) => {
                this.additionalAttachments = attachments;
                attachments.forEach((attachment) => {
                    this.additionalAttachmentsForm.addControl(attachment.id, new FormControl<boolean>(true));
                });
            });
    }

    private fetchExportTypes() {
        this.dataService
            .getExportTypes(this.appService.getSalesOrg())
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: (types: string[]) => (this.availableExportFormats = types),
                error: () => this.onError('NEXT_STEPS.SNACK_BAR.FETCH_EXPORT_FORMATS.ERROR'),
            });
    }

    private setSchemeDwt() {
        return this.appService.getSalesOrg() === '5000' ? this.getSelectedSchemes(SchemeType.dwt) : [];
    }
}
